"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Sun, CloudRain, Mountain } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Extreme Peak vs Monsoon Volatility",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Darjeeling experiences compressed high-demand windows during spring and autumn, followed by sharp monsoon dips. We institute dynamic minimum length-of-stay (MLOS) parameters and tier-based advance booking curves to preserve ADR during surges and sustain occupancy in shoulder periods.",
  metricLabel: "Strategy focus",
  metricValue: "Dynamic MLOS & forward pace-based rate pacing"
}, {
  id: 2,
  title: "OTA Rate Wars & Wholesaler Leakage",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Local competition across Mall Road, Gandhi Road, and Lebong often leads to reactionary OTA discounting. Our algorithmic monitoring scans MakeMyTrip, Booking.com, and Agoda intraday, enforcing price fences and preserving boutique rate integrity.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA tracking & structured price fencing"
}, {
  id: 3,
  title: "Direct Conversion & Brand Equity",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Mitigate 18% to 25% commissions lost to OTAs and B2B aggregators. We align Google Hotel Ads, meta-search rate parity, and curated experiential packages (tea estate tours, sunrise view guarantees) to accelerate profitable direct bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Direct booking engine yield & meta-search parity"
}];
const seasonalitySeasons = [{
  season: "Spring & Pre-Monsoon Surge",
  months: "March – May",
  icon: Sun,
  demand: "Peak High Yield",
  strategy: "Strict minimum 2-3 night stays, premium room unbundling, early-bird price hikes for Kanchenjunga view inventory."
}, {
  season: "Monsoon & Low Inflow",
  months: "June – August",
  icon: CloudRain,
  demand: "Shoulder & Base Fill",
  strategy: "Corporate retreats, wellness packages, flexible cancellation tiers, and long-stay tea tourism packages."
}, {
  season: "Autumn & Clear Sky Surge",
  months: "September – December",
  icon: Mountain,
  demand: "High Volume & Premium ADR",
  strategy: "Aggressive compression pricing, non-refundable discount fences for last-minute leisure, and closed-to-arrival gates."
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapedarjeeling" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Darjeeling
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Darjeeling presents a distinct high-altitude hospitality micro-market characterized by compressed spring and autumn demand peaks, extreme monsoon seasonality, and heritage tea estate boutique competition. Sunil Singh designs high-precision revenue architectures calibrated for hill stations and luxury retreats.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-12">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Demand & Yield Framework Block */}
        <div className="rounded-xl border border-border bg-card/60 p-6 sm:p-8 md:p-10 mb-12 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 mb-6 border-b border-border/80">
            <div>
              <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                Strategic Seasonality Matrix
              </span>
              <h3 className="font-serif text-2xl font-bold text-foreground mt-1">
                Darjeeling Annual Demand & Dynamic Yield Calendar
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Hill station revenue optimization requires customized yield rules per micro-season to protect peak ADR and stabilize baseline RevPAR.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {seasonalitySeasons.map((item, index) => {
            const Icon = item.icon;
            return <div key={item.season} data-index={index} className="rounded-lg border border-border/70 bg-background/80 p-5 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="inline-flex size-10 items-center justify-center rounded-md bg-secondary/15 text-secondary">
                        <Icon className="size-5" />
                      </div>
                      <Badge variant="outline" className="text-[11px] font-medium border-secondary/30">
                        {item.demand}
                      </Badge>
                    </div>
                    <h4 className="font-semibold text-base text-foreground mb-1">
                      {item.season}
                    </h4>
                    <p className="text-xs font-mono text-muted-foreground mb-3">
                      {item.months}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.strategy}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-10 md:mt-14 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Darjeeling hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}